/**
 * agentIcons — the shared catalogue behind every agent-capability icon.
 *
 * Two consumers, one vocabulary:
 *   - cameras_list rows show one small icon per agent the camera has enabled
 *     (Figma 1420:2890);
 *   - the camera_settings rail marks each agent tab with the same icon,
 *     larger and at full contrast.
 *
 * Kept free of React/lucide so it runs under the node-env vitest suite; the
 * `key` → icon component lookup lives in components/occurra/AgentCapabilityIcon.
 * Each agent id is resolved to its capability *group* (agents that share a
 * group are variants of one capability — e.g. object detection vs its
 * segmentation variant — and collapse to a single icon), then matched against
 * the ordered catalogue below so icons render in a stable order regardless of
 * enable order.
 */

import type { Agent } from '@/types/agents';
import type { FlatDevice } from '@/types/devices';

// Ordered: motion, objects, scene embedding, scene insight (matches Figma).
// `modeTitle` names the working mode gated by this capability ("When motion
// is detected" cards in the agent settings) — absent means the generic
// fallback copy.
type CapabilityKey =
  'motion' | 'objects' | 'sceneEmbedding' | 'sceneInsight' | 'lineCrossing';

type CapabilityGroup =
    | 'motion_detection' |
    'object_detection' |
    'occurra.scene_embedding' |
    'occurra.scene_insight' |
    'line_crossing';

interface AgentCapability {
    key: CapabilityKey;
    group: CapabilityGroup;
    label: string;
    modeTitle?: string;
}

export const AGENT_CAPABILITIES: AgentCapability[] = [
    {
        key: 'motion', group: 'motion_detection', label: 'Motion detection',
        modeTitle: 'When motion is detected',
    },
    {
        key: 'objects', group: 'object_detection', label: 'Object detection',
        modeTitle: 'When objects are detected',
    },
    { key: 'sceneEmbedding', group: 'occurra.scene_embedding', label: 'Scene embedding' },
    { key: 'sceneInsight', group: 'occurra.scene_insight', label: 'Scene insight' },
    { key: 'lineCrossing', group: 'line_crossing', label: 'Line crossing' },
];

/**
 * Build `agentId → capability group` from the agents list. `manifest.agentGroup`
 * falls back to the manifest id when empty — mirrors agentMenus' getManifestGroup.
 */
export function getAgentGroups(agents: readonly Agent[]): Record<string, string> {
    const groups: Record<string, string> = {};
    for (const { manifest } of agents) {
        groups[manifest.id] = manifest.agentGroup || manifest.id;
    }
    return groups;
}

/**
 * Ordered subset of AGENT_CAPABILITIES that the camera has enabled.
 *
 * An id absent from `agentGroups` resolves to itself, so ids that are already
 * their own group (motion_detection, occurra.scene_*) still match before the
 * agents list has loaded.
 */
export function getCameraAgentCapabilities(
    camera: Pick<FlatDevice, 'enabledAgents'>,
    agentGroups?: Record<string, string>,
): AgentCapability[] {
    const enabledIds = new Set(
        camera.enabledAgents.map((id) => (agentGroups && agentGroups[id]) || id),
    );
    return AGENT_CAPABILITIES.filter((c) => enabledIds.has(c.group));
}

/**
 * The capability a group id maps to, or null when the group has no icon.
 * Agent settings tabs are keyed by group (an agent menu's id IS its group),
 * so this is how the rail resolves a tab to its icon.
 */
export function getCapabilityForGroup(group: string): AgentCapability | null {
    return AGENT_CAPABILITIES.find((c) => c.group === group) || null;
}
